(function () {
  'use strict';

  let currentKeyword = '';
  let currentVariantId = '';
  let activeIndex = -1;

  let observer = null;
  let filterTimer = null;
  let suppressReset = false;

  let hideVariantsEnabled = false;
  let variantAttachedMediaIds = [];

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────

  function stripEmoji(str) {
    return str.replace(
      /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu,
      ''
    ).trim();
  }

  function extractKeyword(raw) {
    if (!raw) return '';

    const cleaned = stripEmoji(raw.toLowerCase().trim());
    const parts = cleaned.split(/\s*-\s*/);

    return parts[parts.length - 1].trim();
  }

  function altMatches(rawAlt, kw) {

    if (!rawAlt || rawAlt.trim() === '') return true;
    if (!kw) return true;

    const alt = stripEmoji(rawAlt.toLowerCase().trim());
    const k = stripEmoji(kw.toLowerCase().trim());

    if (!k) return true;

    if (alt === k) return true;

    const escapedKw =
      k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    if (
      new RegExp('(\\s*-\\s*|\\s+)' + escapedKw + '$')
        .test(alt)
    ) {
      return true;
    }

    return false;
  }

  // ─────────────────────────────────────────────────────────────
  // DOM
  // ─────────────────────────────────────────────────────────────

  function getGallery() {
    return document.querySelector('media-gallery');
  }

  function getScroller() {
    return getGallery()?.querySelector('slideshow-slides');
  }

  function getConfig() {
    const el = document.querySelector(
      'script[id^=\"variant-media-map-\"]'
    );

    if (!el) return {};

    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  }

  function getVariantIdFromUrl() {
    return new URLSearchParams(window.location.search)
      .get('variant') || '';
  }

  function getCheckedVariantId() {
    return document.querySelector(
      'variant-picker input[data-current-checked=\"true\"][data-variant-id]'
    )?.dataset.variantId || '';
  }

  function getAllSlides(gallery) {
    return Array.from(
      gallery.querySelectorAll(
        'slideshow-slide[data-media-alt]'
      )
    );
  }

  function getAllThumbs(gallery) {
    return Array.from(
      gallery.querySelectorAll(
        '.slideshow-controls__thumbnail[data-media-alt]'
      )
    );
  }

  function parseIndex(el) {
    const m =
      (el?.getAttribute('on:click') || '')
        .match(/\/select\/(\d+)/);

    return m ? parseInt(m[1], 10) : -1;
  }

  function getMediaId(slide) {
    return slide
      ?.querySelector('[data-media-id]')
      ?.dataset.mediaId || '';
  }

  // ─────────────────────────────────────────────────────────────
  // Variant keyword
  // ─────────────────────────────────────────────────────────────

  function getVariantKeyword(variantId) {

    const config = getConfig();

    const entry =
      config.variantMediaMap?.[variantId];

    if (!entry) return '';

    let raw = '';

    if (typeof entry === 'string') {
      raw = entry;
    } else {
      raw = entry.keyword || entry.alt || '';
    }

    return extractKeyword(raw);
  }

  // ─────────────────────────────────────────────────────────────
  // CSS
  // ─────────────────────────────────────────────────────────────

  function injectCSS() {

    if (
      document.getElementById('vmf-style')
    ) return;

    const s = document.createElement('style');

    s.id = 'vmf-style';

    s.textContent = `
      slideshow-slide[data-variant-hidden="true"]{
        width:0!important;
        min-width:0!important;
        opacity:0!important;
        overflow:hidden!important;
        pointer-events:none!important;
      }

      .slideshow-controls__thumbnail[data-variant-hidden="true"]{
        display:none!important;
      }
    `;

    document.head.appendChild(s);
  }

  // ─────────────────────────────────────────────────────────────
  // Filter
  // ─────────────────────────────────────────────────────────────

  function shouldShowSlide(slide) {

    const alt =
      slide.dataset.mediaAlt || '';

    if (
      !altMatches(alt, currentKeyword)
    ) {
      return false;
    }

    const mediaId = getMediaId(slide);

    if (
      hideVariantsEnabled &&
      mediaId
    ) {

      const attached =
        variantAttachedMediaIds.includes(mediaId);

      if (
        attached &&
        slide.style.display === 'none'
      ) {
        return false;
      }
    }

    return true;
  }

  function applyFilter() {

    const gallery = getGallery();

    if (!gallery) return;

    getAllSlides(gallery).forEach(slide => {

      const show =
        shouldShowSlide(slide);

      if (show) {
        slide.removeAttribute(
          'data-variant-hidden'
        );
      } else {
        slide.setAttribute(
          'data-variant-hidden',
          'true'
        );
      }

      slide.style.display = '';
    });

    getAllThumbs(gallery).forEach(thumb => {

      const show = altMatches(
        thumb.dataset.mediaAlt,
        currentKeyword
      );

      if (show) {
        thumb.removeAttribute(
          'data-variant-hidden'
        );
      } else {
        thumb.setAttribute(
          'data-variant-hidden',
          'true'
        );
      }

    });
  }

  // ─────────────────────────────────────────────────────────────
  // Navigation
  // ─────────────────────────────────────────────────────────────

  function scrollToSlide(gallery, absoluteIndex) {

    const slides =
      getAllSlides(gallery);

    const target =
      slides[absoluteIndex];

    const scroller =
      getScroller();

    if (!target || !scroller) return;

    activeIndex = absoluteIndex;

    suppressReset = true;

    clearTimeout(window.__vmfResetTimer);

    window.__vmfResetTimer =
      setTimeout(() => {
        suppressReset = false;
      }, 400);

    scroller.scrollTo({
      left: target.offsetLeft,
      behavior: 'smooth'
    });

    updateThumbActive(
      gallery,
      absoluteIndex
    );
  }

  function updateThumbActive(
    gallery,
    absoluteIndex
  ) {

    getAllThumbs(gallery).forEach(t => {

      t.setAttribute(
        'aria-selected',
        parseIndex(t) === absoluteIndex
          ? 'true'
          : 'false'
      );

    });
  }

  function goToFirstVisible(gallery) {

    const visible =
      getAllThumbs(gallery)
        .filter(t =>
          !t.hasAttribute(
            'data-variant-hidden'
          )
        );

    if (!visible.length) return;

    const idx =
      parseIndex(visible[0]);

    if (idx < 0) return;

    scrollToSlide(
      gallery,
      idx
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Gallery update
  // ─────────────────────────────────────────────────────────────

  function updateGallery(reset = false) {

    applyFilter();

    requestAnimationFrame(() => {

      const gallery =
        getGallery();

      if (!gallery) return;

      if (suppressReset) {
        updateThumbActive(
          gallery,
          activeIndex
        );
        return;
      }

      if (
        reset ||
        activeIndex < 0
      ) {
        goToFirstVisible(gallery);
      }

    });
  }

  // ─────────────────────────────────────────────────────────────
  // Clicks
  // ─────────────────────────────────────────────────────────────

  function attachClicks(gallery) {

    gallery.addEventListener(
      'click',
      (e) => {

        const thumb =
          e.target.closest(
            '.slideshow-controls__thumbnail'
          );

        if (!thumb) return;

        if (
          thumb.getAttribute(
            'data-variant-hidden'
          ) === 'true'
        ) {

          e.preventDefault();
          e.stopImmediatePropagation();

          return;
        }

        const idx =
          parseIndex(thumb);

        if (idx < 0) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        scrollToSlide(
          gallery,
          idx
        );

      },
      true
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Observe morphs
  // ─────────────────────────────────────────────────────────────

  function observeGallery(gallery) {

    if (observer) {
      observer.disconnect();
    }

    observer =
      new MutationObserver(() => {

        clearTimeout(filterTimer);

        filterTimer =
          setTimeout(() => {

            updateGallery(false);

          }, 120);

      });

    observer.observe(gallery, {
      childList: true,
      subtree: true
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Variant changes
  // ─────────────────────────────────────────────────────────────

  function handleVariantChange(
    newId,
    force
  ) {

    if (!newId) return;

    const id = String(newId);

    if (
      !force &&
      id === currentVariantId
    ) {
      return;
    }

    currentVariantId = id;

    currentKeyword =
      getVariantKeyword(id);

    activeIndex = -1;

    suppressReset = false;

    updateGallery(true);
  }

  // ─────────────────────────────────────────────────────────────
  // Events
  // ─────────────────────────────────────────────────────────────

  document.addEventListener(
    'change',
    (e) => {

      const input =
        e.target;

      if (
        !(input instanceof HTMLInputElement) ||
        !input.dataset.variantId
      ) {
        return;
      }

      handleVariantChange(
        input.dataset.variantId
      );

    }
  );

  document.addEventListener(
    'variant:update',
    (e) => {

      const id =
        String(
          e.detail?.variant?.id || ''
        ) ||
        String(
          e.detail?.id || ''
        ) ||
        getCheckedVariantId() ||
        getVariantIdFromUrl();

      if (!id) return;

      handleVariantChange(
        id,
        true
      );

    }
  );

  // ─────────────────────────────────────────────────────────────
  // Init
  // ─────────────────────────────────────────────────────────────

  function init() {

    injectCSS();

    const config =
      getConfig();

    hideVariantsEnabled =
      config.hideVariantsEnabled || false;

    variantAttachedMediaIds =
      (
        config.variantAttachedMediaIds || []
      ).map(String);

    const gallery =
      getGallery();

    if (!gallery) return;

    attachClicks(gallery);

    observeGallery(gallery);

    currentVariantId =
      getVariantIdFromUrl() ||
      getCheckedVariantId();

    currentKeyword =
      getVariantKeyword(
        currentVariantId
      );

    updateGallery(true);
  }

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  } else {

    init();

  }

})();